import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search } from "lucide-react";
import { api, fmt } from "../lib/api";
import { lp, productAlt } from "../lib/localize";
import { useLang } from "../i18n/LanguageContext";
import { FadeUp } from "../components/motion";

const PAGE_SIZE = 48;

// Vue grille produits : catalogue web (catalogueNo), page catégorie SEO (fixedCategory)
// ou page d'occasion (productsOverride = produits déjà fournis par l'API).
export default function CatalogueGrid({ catalogueNo, fixedCategory, productsOverride }) {
  const { lang, t, ts } = useLang();
  const [products, setProducts] = useState(null);
  const [category, setCategory] = useState("");
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(PAGE_SIZE);

  useEffect(() => {
    setProducts(null);
    setCategory("");
    setQuery("");
    setShown(PAGE_SIZE);
    if (productsOverride) { setProducts(productsOverride); return; }
    const params = fixedCategory ? { category: fixedCategory } : { catalogue_no: catalogueNo };
    api.get("/products", { params })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]));
  }, [catalogueNo, fixedCategory, productsOverride]);

  const categories = useMemo(() => {
    const seen = new Map();
    (products || []).forEach((p) => {
      const label = lang === "en" ? (p.category_en || p.category) : p.category;
      if (label && !seen.has(label)) seen.set(label, p.category);
    });
    return [...seen.keys()].sort((a, b) => a.localeCompare(b, "fr"));
  }, [products, lang]);

  const filtered = useMemo(() => {
    let list = products || [];
    if (category) list = list.filter((p) => p.category === category);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((p) =>
        [p.name, p.name_en, p.sku, p.category, p.category_en].some((v) => (v || "").toLowerCase().includes(q)));
    }
    return list;
  }, [products, category, query]);

  if (!products) {
    return <div className="flex items-center justify-center py-32" data-testid="grid-loading"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;
  }

  return (
    <div data-testid="catalogue-grid">
      {/* Recherche */}
      <FadeUp delay={0.05} className="mb-5">
        <div className="relative max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ivory/65" />
          <input
            data-testid="grid-search" value={query}
            onChange={(e) => { setQuery(e.target.value); setShown(PAGE_SIZE); }}
            placeholder={ts("catalogue.search_placeholder")}
            className="w-full bg-surface border border-ivory/15 rounded-full pl-11 pr-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
          />
        </div>
      </FadeUp>

      {/* Filtres par catégorie (masqués sur les pages catégorie dédiées) */}
      {!fixedCategory && (
      <FadeUp delay={0.08} className="mb-8">
        <div className="flex flex-wrap gap-2" data-testid="grid-categories">
          <button onClick={() => { setCategory(""); setShown(PAGE_SIZE); }} data-testid="grid-cat-all"
            className={`px-4 py-1.5 rounded-full text-xs border transition-colors duration-300 ${!category ? "border-gold bg-gold text-ink font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/50 hover:text-gold"}`}>
            {t("catalogue.grid_all")}
          </button>
          {categories.map((c) => (
            <button key={c} onClick={() => { setCategory(c); setShown(PAGE_SIZE); }}
              data-testid={`grid-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-4 py-1.5 rounded-full text-xs border transition-colors duration-300 ${category === c ? "border-gold bg-gold text-ink font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/50 hover:text-gold"}`}>
              {c}
            </button>
          ))}
        </div>
      </FadeUp>
      )}

      <p className="text-xs text-ivory/65 mb-4" data-testid="grid-count">{t("catalogue.grid_count", { n: filtered.length })}</p>

      {/* Grille produits */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
        {filtered.slice(0, shown).map((p, i) => (
          <FadeUp key={p.id} delay={Math.min(i % 8, 6) * 0.05}>
            <Link to={`/produit/${p.id}`} data-testid={`grid-product-${i}`}
              className="group block bg-surface border border-ivory/10 rounded-xl overflow-hidden hover:border-gold/50 transition-colors duration-300">
              <div className="bg-white aspect-square flex items-center justify-center p-4">
                <img src={p.image} alt={productAlt(p, lang)} loading="lazy"
                  className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-4">
                <p className="text-sm text-ivory leading-snug line-clamp-2 min-h-[2.5em]">{lp(p, lang).name}</p>
                <div className="flex items-center justify-between mt-2 gap-2">
                  <p className="text-gold font-serif" data-testid={`grid-price-${i}`}>
                    {t("catalogue.grid_from", { price: fmt(p.base_price) })}
                  </p>
                  <span className="text-[10px] text-ivory/65 tracking-widest uppercase">{p.sku}</span>
                </div>
                {p.min_qty > 1 && (
                  <p className="text-[10px] text-champagne/70 mt-1">{t("product.min_qty", { n: p.min_qty })}</p>
                )}
              </div>
            </Link>
          </FadeUp>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-ivory/70 py-16" data-testid="grid-empty">{t("catalogue.grid_empty")}</p>
      )}
      {filtered.length > shown && (
        <div className="text-center mt-10">
          <button onClick={() => setShown((s) => s + PAGE_SIZE)} data-testid="grid-show-more"
            className="px-8 py-3 rounded-full border border-gold/40 text-gold text-sm hover:bg-gold hover:text-ink transition-colors duration-300">
            {t("catalogue.grid_more", { n: filtered.length - shown })}
          </button>
        </div>
      )}
    </div>
  );
}
